import axios from '@utils/axiosInstance'
import {urlQueryStringBuilder} from '@utils/constantHelpers'

const BASE = process.env.VUE_APP_BACKEND_PUBLIC_URL

const getDefaultState = () => {
  return {
    leaveProcess: null,
    leaveReasons: [],
    openLeaveProcesses: null,
    notRehireableReasons: {},
    checkListOption: {},
    checkListOptions: [],
    checkListOptionsChosen: {},
  }
}
const statusOrder = [
  'WAITING_FOR_IDENTITY_ACCESS_RESPONSE',
  'STATUS_WAITING_FOR_IDENTITY_ACCESS_RESPONSE',
  'WAITING_TO_BE_EXECUTED',
  'WAITING_FOR_OSMOS_RESPONSE',
  'STATUS_WAITING_FOR_OSMOS_RESPONSE',
  'SCHEDULED_FOR_CONVERSION_DATE',
  'SCHEDULED_FOR_EXTENSION_DATE',
  'SCHEDULED_FOR_START_DATE',
  'PENDING_MUTATION_COLLABORATOR_PROCESS',
  'COMPLETED_SUCCESS'
];

const leaveStatus = [
  'WAITING_FOR_IDENTITY_ACCESS_RESPONSE',
  'STATUS_WAITING_FOR_IDENTITY_ACCESS_RESPONSE',
  'WAITING_TO_BE_EXECUTED',
  'WAITING_FOR_OSMOS_RESPONSE',
  'STATUS_WAITING_FOR_OSMOS_RESPONSE',
  'SCHEDULED_FOR_CONVERSION_DATE',
  'SCHEDULED_FOR_EXTENSION_DATE',
  'SCHEDULED_FOR_START_DATE',
  'PENDING_MUTATION_COLLABORATOR_PROCESS',
  'COMPLETED_SUCCESS'
]


export const state = getDefaultState()

export const getters = {}

export const mutations = {
  UPDATE_LEAVE_PROCESS(state, leaveProcess) {
    state.leaveProcess = leaveProcess
  },
  UPDATE_LEAVE_REASONS(state, leaveReasons) {
    state.leaveReasons = leaveReasons
  },
  UPDATE_OPEN_LEAVE_PROCESSES(state, openLeaveProcesses) {
    state.openLeaveProcesses = openLeaveProcesses
  },
  UPDATE_NOT_REHIREABLE_REASONS(state, notRehireableReasons) {
    state.notRehireableReasons = notRehireableReasons
  },
  UPDATE_CHECK_LIST_OPTIONS(state, options) {
    state.checkListOptions = options
  },
  UPDATE_CHECK_LIST_OPTION(state, option) {
    state.checkListOption = option
  },
  UPDATE_CHECK_LIST_OPTIONS_CHOSEN(state, options) {
    state.checkListOptionsChosen = options
  },
  RESET_LEAVE_MANAGER_STATE(state) {
    Object.assign(state, getDefaultState())
  },
}

// Transforms the leave process' JSON property `checkListOptionsChosen`
// into an object compatible with Vue radio groups.
// Example: {1: 1, 2: 0, 3: null}
function getCheckListOptionsChosenFromLeaveProcess(store, process) {
  if (! process) {
    return
  }
  const result = {}
  const length =  Object.keys(process.checkListOptionsChosen).length
  for (let index = 0; index < length; index++) {
    const chosen = process.checkListOptionsChosen[index]
    if (chosen.optionId !== undefined) {
      result[chosen.optionId] = chosen.chosenValue
    }
  }
  store.commit('UPDATE_CHECK_LIST_OPTIONS_CHOSEN', result)
}

export const actions = {
  // LEAVE PROCESS
  // GET
  async fetchLeaveProcess(store, processUuid) {
    try {
      const res = await axios.get(
        `${BASE}/mx/api/v2/leaveProcesses/${processUuid}`,
      )
      store.commit('UPDATE_LEAVE_PROCESS', res.data)
      getCheckListOptionsChosenFromLeaveProcess(store, res.data)
    } catch (error) {
      throw new Error(
        `API Error occurred in ${BASE}/mx/api/v2/leaveProcesses/${processUuid}`,
      )
    }
  },
  // OPEN LEAVE PROCESSES
  // GET
  async fetchOpenLeaveProcesses(store, payload = {}) {
    try {
      const res = await axios.get(
        `${BASE}/mx/api/v2/leaveProcesses.common${urlQueryStringBuilder(
          payload,
        )}`,
      )
      store.commit('UPDATE_OPEN_LEAVE_PROCESSES', res.data)
      if(res.data.data.length >= 1){

        const arr = res.data.data;
        arr.sort((a, b) => {
          return statusOrder.indexOf(a.currentStatus) - statusOrder.indexOf(b.currentStatus);
        });

        return arr
      }
    } catch (error) {
      throw new Error(
        `API Error occurred in mx/api/v2/leaveProcesses.common${urlQueryStringBuilder(
          payload,
        )}.`,
      )
    }
  },
  // OPEN LEAVE PROCESSES OF SITE
  // GET
  async fetchSiteOpenLeaveProcesses(store, siteCode) {
    try {
      const res = await axios.get(
        `${BASE}/mx/api/v2/sites/${siteCode}/leaveProcesses.common`,
      )
      store.commit('UPDATE_OPEN_LEAVE_PROCESSES', res.data)
      if(res.data.data.length >= 1){

        const arr = res.data.data;
        arr.sort((a, b) => {
          return leaveStatus.indexOf(a.currentStatus) - leaveStatus.indexOf(b.currentStatus);
        });

        return arr
      }
    } catch (error) {
      throw new Error(
        `API Error occurred in mx/api/v2/sites/${siteCode}/leaveProcesses.common`,
      )
    }
  },
  // LEAVE ALL REASONS
  // GET
  async fetchLeaveAllReasons(store) {
    const url = `${BASE}/mx/api/v2/leaveReasons`
    try {
      const res = await axios.get(url)
      store.commit('UPDATE_LEAVE_REASONS', res.data)
    } catch (e) {
      throw Error(`API Error occurred in ${url}`)
    }
  },
  // LEAVE REASONS
  // GET
  async fetchLeaveReasons(store, collaboratorId) {
    const queryParams = collaboratorId
      ? `?filter[collaboratorUuid]=${collaboratorId}`
      : ''
    const url = `${BASE}/mx/api/v2/applicableLeaveReasons${queryParams}`
    try {
      const res = await axios.get(url)
      store.commit('UPDATE_LEAVE_REASONS', res.data)
    } catch (e) {
      throw Error(`API Error occurred in ${url}`)
    }
  },
  // NOT REHIREABLE REASONS
  async fetchNotRehireableReasons(store) {
    try {
      const res = await axios.get(`${BASE}/mx/api/v2/notRehireableReasons/all`)
      store.commit('UPDATE_NOT_REHIREABLE_REASONS', res.data.data)
    } catch (error) {
      throw new Error(
        'API Error occurred in /mx/api/v2/notRehireableReasons/all.',
      )
    }
  },

  // ///////
  // POSTS
  // //////

  // NEW LEAVE
  // POST
  // Theses ID numbers are based on the core_contract_leave_reason table. Change them is necessary.
  async startLeaveProcess(store, { payload, leaveReasonName }) {
    const url = `${BASE}/mx/api/v2/contractManager/leave.startLeaveProcess.${leaveReasonName}`
    try {
      return await axios.post(url, payload)
    } catch (e) {
      throw Error(`API Error occurred at ${url}`)
    }
  },
  // Send leave notify
  async notifyLeaveProcess(store,  payload ) {
    const url = `${BASE}/mx/api/v2/contractManager/leave.notify`
    try {
      return await axios.post(url, payload)
    } catch (e) {
      throw Error(`API Error occurred at ${url}`)
    }
  },
  // CANCEL LEAVE PROCESS
  // POST
  async cancelLeaveProcess(store, payload) {
    const url = `${BASE}/mx/api/v2/contractManager/leave.cancel`
    try {
      return await axios.post(url, payload)
    } catch (e) {
      throw Error(`API Error occurred at ${url}`)
    }
  },
  // LEAVE SURVEY
  // POST
  async leaveSurvey(store, payload) {
    const url = `${BASE}/mx/api/v2/contractManager/leave.survey`
    try {
      return await axios.post(url, payload)
    } catch (e) {
      throw Error(`API Error occurred at ${url}`)
    }
  },
  async sendCollaboratorLeaveSurvey({ store }, payload) {
    const url = `${BASE}/mx/api/v2/contractManager/leave.send`
    try {
      return await axios.post(url, payload)
    } catch (e) {
      throw Error(`API Error occurred at ${url}`)
    }

  },
  // CHECK LIST OPTIONS ADMIN
  async fetchCheckListOptions(store) {
    // index
    const url = `${BASE}/mx/api/v2/contractManager/leave.checkListOptions`
    try {
      const res = await axios.get(url)
      store.commit('UPDATE_CHECK_LIST_OPTIONS', res.data)
      return res.data
    } catch (error) {
      throw new Error(`API Error occurred at ` + url)
    }
  },
  // CHECK LIST OPTIONS ADMIN
  async fetchDashboard(store) {
    // index
    const url = `${BASE}/mx/api/v2/contractManager/leave.dashboard`
    try {
      const res = await axios.get(url)
      return res.data
    } catch (error) {
      throw new Error(`API Error occurred at ` + url)
    }
  },
  async fetchCheckListOption(store, id) {
    // show
    const url = `${BASE}/mx/api/v2/contractManager/leave.checkListOptions/${id}`
    try {
      const res = await axios.get(url)
      store.commit('UPDATE_CHECK_LIST_OPTION', res.data)
      return res.data
    } catch (error) {
      throw new Error(`API Error occurred at ` + url)
    }
  },
  async cleanCheckListOptionsChosen(store) {
    store.commit('UPDATE_CHECK_LIST_OPTIONS_CHOSEN', {})
  },
  async checkListOptionCreateAction(store, payload) {
    const url = `${BASE}/mx/api/v2/contractManager/leave.checkListOptions.create`
    try {
      return await axios.post(url, payload)
    } catch (e) {
      throw Error(`API Error occurred at ` + url)
    }
  },
  async checkListOptionUpdateAction(store, payload) {
    const url = `${BASE}/mx/api/v2/contractManager/leave.checkListOptions.update`
    try {
      return await axios.post(url, payload)
    } catch (e) {
      throw Error(`API Error occurred at ` + url)
    }
  },
  async checkListOptionDeleteAction(store, payload) {
    const url = `${BASE}/mx/api/v2/contractManager/leave.checkListOptions.delete`
    try {
      return await axios.post(url, payload)
    } catch (e) {
      throw Error(`API Error occurred at ` + url)
    }
  },
}
